import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/api';

// Create the authentication context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

// Provider component that wraps the app and makes auth object available to any child component that calls useAuth()
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Check if user is already logged in (from localStorage)
  useEffect(() => {
    const user = localStorage.getItem('user');
    if (user) {
      setCurrentUser(JSON.parse(user));
    }
    setLoading(false);
  }, []);

  // Login function
  const login = async (usernameOrEmail, password) => {
    setError('');
    setLoading(true);
    
    console.log('AuthContext: Calling login API');
    // Call the admin login API
    const result = await authAPI.login(usernameOrEmail, password);
    console.log('AuthContext: Login API response received', result);
    
    if (result.success) {
      // Store user data in localStorage for persistence
      localStorage.setItem('user', JSON.stringify(result.data));
      
      // First set loading to false to ensure state updates are batched properly
      setLoading(false);
      
      // Then update currentUser state to trigger navigation
      setCurrentUser(result.data);
      
      return true; // Success
    } else {
      // Handle specific error messages
      let errorMessage = 'Failed to login';
      
      if (result.status === 403) {
        errorMessage = 'Unauthorized: Admin access required';
      } else if (result.status === 401) {
        errorMessage = 'Invalid credentials. Please check your username and password.';
      } else if (result.message) {
        errorMessage = result.message;
      }
      
      // Set the error message
      setError(errorMessage);
      console.log('AuthContext: Error set to', errorMessage);
      
      // Make sure currentUser remains null on failed login
      // This ensures React Router won't try to navigate
      setCurrentUser(null);
      
      setLoading(false);
      return false; // Failure
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await authAPI.logout();
      setCurrentUser(null);
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  // Value object that will be passed to any consuming components
  const value = {
    currentUser,
    login,
    logout,
    error,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
